/**
 * @fileoverview FileListModule - Reusable file list component for knowledge base files
 * 
 * This module provides a reusable file list component with enhanced functionality including:
 * - Edit capability for text files
 * - Real-time sync status display
 * - File type icons and formatted size display
 * - Parrot usage information display
 * - Bulk operations support
 * 
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 3.6
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useToast } from '../../contexts/ToastContext';
import { useApi } from '../../hooks/useApi';
import { useKnowledgeBaseErrorHandling } from '../../hooks/useErrorHandling';
import { formatFileSize } from '../../utils/fileUtils.js';
import { getThemeClasses, getButtonClasses, getCardClasses } from '../../utils/themeUtils';
import LoadingIndicator, { OperationStatus } from '../../components/LoadingIndicator';
import ErrorBoundary from '../../components/ErrorBoundary';
import EnhancedKnowledgeBaseSyncStatus from '../../components/EnhancedKnowledgeBaseSyncStatus';
import FileDeleteConfirmationDialog from '../../components/FileDeleteConfirmationDialog';

// File type icons mapping
const FILE_TYPE_ICONS = {
  'pdf': {
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
      </svg>
    ),
    color: 'text-red-500',
    bgColor: 'bg-red-100 dark:bg-red-900/30'
  },
  'docx': {
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
      </svg>
    ),
    color: 'text-blue-500',
    bgColor: 'bg-blue-100 dark:bg-blue-900/30'
  },
  'txt': {
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 110 2H4a1 1 0 01-1-1zM4 15a1 1 0 100 2h12a1 1 0 100-2H4z" clipRule="evenodd" />
      </svg>
    ),
    color: 'text-gray-500',
    bgColor: 'bg-gray-100 dark:bg-gray-900/30'
  },
  'html': {
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M12.316 3.051a1 1 0 01.633 1.265l-4 12a1 1 0 11-1.898-.632l4-12a1 1 0 011.265-.633zM5.707 6.293a1 1 0 010 1.414L3.414 10l2.293 2.293a1 1 0 11-1.414 1.414l-3-3a1 1 0 010-1.414l3-3a1 1 0 011.414 0zm8.586 0a1 1 0 011.414 0l3 3a1 1 0 010 1.414l-3 3a1 1 0 11-1.414-1.414L16.586 10l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
      </svg>
    ),
    color: 'text-orange-500',
    bgColor: 'bg-orange-100 dark:bg-orange-900/30'
  },
  'text': {
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2H4zm0 2h12v8H4V6z" clipRule="evenodd" />
      </svg>
    ),
    color: 'text-green-500',
    bgColor: 'bg-green-100 dark:bg-green-900/30'
  },
  'default': {
    icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
      </svg>
    ),
    color: 'text-gray-500',
    bgColor: 'bg-gray-100 dark:bg-gray-900/30'
  }
};

/**
 * FileListModule Props Interface
 * @typedef {Object} FileListModuleProps
 * @property {Array} files - Array of knowledge base files to display
 * @property {Function} onFileSelect - Callback when file is selected (fileId, selected)
 * @property {Function} onFileDelete - Callback when file is deleted (fileId)
 * @property {Function} onFileEdit - Callback when file edit is requested (fileId) - NEW for text files
 * @property {Function} onSyncRetry - Callback when sync retry is requested (fileId)
 * @property {Function} onBulkOperation - Callback for bulk operations (operation, fileIds)
 * @property {boolean} showSyncStatus - Whether to show sync status (default: true)
 * @property {boolean} allowSelection - Whether to show selection checkboxes (default: false)
 * @property {boolean} showUsage - Whether to show parrot usage information (default: true)
 * @property {boolean} allowEdit - Whether to show edit button for text files (default: true)
 * @property {boolean} allowBulkOperations - Whether to show bulk operation controls (default: false)
 * @property {string} context - Context for the file list ('dashboard' | 'assignment' | 'wizard')
 * @property {Object} filterOptions - Filter configuration object
 * @property {Array} selectedFiles - Array of currently selected file IDs
 * @property {boolean} loading - Whether the file list is loading
 * @property {string} emptyMessage - Custom message when no files are found
 */

const FileListModule = ({
  files = [],
  onFileSelect = null,
  onFileDelete = null,
  onFileEdit = null,
  onSyncRetry = null,
  onBulkOperation = null,
  showSyncStatus = true,
  allowSelection = false,
  showUsage = true,
  allowEdit = true,
  allowBulkOperations = false,
  context = 'dashboard',
  filterOptions = {},
  selectedFiles = [],
  loading = false,
  emptyMessage = null
}) => {
  const { getToken } = useAuth();
  const { showSuccess, showError } = useToast();
  const { execute } = useApi();
  const errorHandling = useKnowledgeBaseErrorHandling();
  
  // State management
  const [selectedFileIds, setSelectedFileIds] = useState(new Set(selectedFiles));
  const [deletingFiles, setDeletingFiles] = useState(new Set());
  const [operationStatus, setOperationStatus] = useState(new Map());
  const [bulkOperationInProgress, setBulkOperationInProgress] = useState(false);
  
  // Update selected files when prop changes
  React.useEffect(() => {
    setSelectedFileIds(new Set(selectedFiles));
  }, [selectedFiles]);
  
  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    file: null,
    parrotUsage: []
  });

  // Get file type icon and styling
  const getFileTypeIcon = useCallback((file) => {
    let fileType = 'default';
    
    if (file.type === 'text') {
      fileType = 'text';
    } else if (file.mime_type) {
      if (file.mime_type.includes('pdf')) fileType = 'pdf';
      else if (file.mime_type.includes('wordprocessingml')) fileType = 'docx';
      else if (file.mime_type.includes('text/plain')) fileType = 'txt';
      else if (file.mime_type.includes('text/html')) fileType = 'html';
    } else if (file.name) {
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (['pdf', 'docx', 'txt', 'html'].includes(extension)) {
        fileType = extension;
      }
    }
    
    return FILE_TYPE_ICONS[fileType] || FILE_TYPE_ICONS.default;
  }, []);

  // File selection handling
  const handleFileSelection = useCallback((fileId, selected) => {
    const newSelectedIds = new Set(selectedFileIds);
    if (selected) {
      newSelectedIds.add(fileId);
    } else {
      newSelectedIds.delete(fileId);
    }
    setSelectedFileIds(newSelectedIds);
    
    if (onFileSelect) {
      onFileSelect(fileId, selected);
    }
  }, [selectedFileIds, onFileSelect]);

  // Select all files
  const handleSelectAll = useCallback((selected) => {
    const newSelectedIds = selected ? new Set(files.map(f => f.id)) : new Set();
    setSelectedFileIds(newSelectedIds);
    
    if (onFileSelect) {
      files.forEach(file => onFileSelect(file.id, selected));
    }
  }, [files, onFileSelect]);

  // Show delete confirmation dialog
  const showDeleteConfirmation = useCallback((file) => {
    const parrotUsage = file.parrotUsage && file.parrotUsage.parrotNames 
      ? file.parrotUsage.parrotNames 
      : [];
    
    setConfirmDialog({
      isOpen: true,
      file: file,
      parrotUsage: parrotUsage
    });
  }, []);

  // Cancel delete confirmation
  const cancelDelete = useCallback(() => {
    setConfirmDialog({
      isOpen: false,
      file: null,
      parrotUsage: []
    });
  }, []);

  // Confirm file deletion
  const confirmDelete = useCallback(async () => {
    const { file } = confirmDialog;
    if (!file || !onFileDelete) return;

    setDeletingFiles(prev => new Set([...prev, file.id]));
    setOperationStatus(prev => new Map(prev.set(`delete_${file.id}`, { 
      status: 'running', 
      message: `Deleting "${file.name}"...` 
    })));
    
    try {
      await onFileDelete(file.id);
      
      // Close dialog
      setConfirmDialog({
        isOpen: false,
        file: null,
        parrotUsage: []
      });
      
      // Update operation status
      setOperationStatus(prev => new Map(prev.set(`delete_${file.id}`, { 
        status: 'success', 
        message: `"${file.name}" deleted successfully` 
      })));
      
    } catch (error) {
      console.error('Failed to delete file:', error);
      
      setOperationStatus(prev => new Map(prev.set(`delete_${file.id}`, { 
        status: 'error', 
        message: `Failed to delete "${file.name}"`,
        onRetry: () => confirmDelete()
      })));
      
      // Close dialog on error
      setConfirmDialog({
        isOpen: false,
        file: null,
        parrotUsage: []
      });
    } finally {
      setDeletingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(file.id);
        return newSet;
      });
      
      // Clear operation status after delay
      setTimeout(() => {
        setOperationStatus(prev => {
          const newMap = new Map(prev);
          newMap.delete(`delete_${file.id}`);
          return newMap;
        });
      }, 3000);
    }
  }, [confirmDialog, onFileDelete]);

  // Handle edit file (for text files)
  const handleEditFile = useCallback((file) => {
    if (onFileEdit && (file.type === 'text' || file.mime_type === 'text/plain')) {
      onFileEdit(file.id);
    }
  }, [onFileEdit]);

  // Handle sync retry
  const handleSyncRetry = useCallback(async (fileId) => {
    if (onSyncRetry) {
      await onSyncRetry(fileId);
    }
  }, [onSyncRetry]);

  // Handle bulk operations
  const handleBulkOperation = useCallback(async (operation) => {
    if (!onBulkOperation || selectedFileIds.size === 0) return;
    
    setBulkOperationInProgress(true);
    setOperationStatus(prev => new Map(prev.set('bulk_operation', { 
      status: 'running', 
      message: `Performing ${operation} on ${selectedFileIds.size} files...` 
    })));
    
    try {
      await onBulkOperation(operation, Array.from(selectedFileIds));
      
      setOperationStatus(prev => new Map(prev.set('bulk_operation', { 
        status: 'success', 
        message: `${operation} completed for ${selectedFileIds.size} files` 
      })));
      
      // Clear selection after successful bulk operation
      setSelectedFileIds(new Set());
      
    } catch (error) {
      console.error('Bulk operation failed:', error);
      
      setOperationStatus(prev => new Map(prev.set('bulk_operation', { 
        status: 'error', 
        message: `Failed to ${operation} some files`,
        onRetry: () => handleBulkOperation(operation)
      })));
    } finally {
      setBulkOperationInProgress(false);
      
      // Clear operation status after delay
      setTimeout(() => {
        setOperationStatus(prev => {
          const newMap = new Map(prev);
          newMap.delete('bulk_operation');
          return newMap;
        });
      }, 5000);
    }
  }, [onBulkOperation, selectedFileIds]);

  // Memoized computed values
  const { allSelected, someSelected, hasFailedFiles } = useMemo(() => {
    const allSelected = files.length > 0 && selectedFileIds.size === files.length;
    const someSelected = selectedFileIds.size > 0 && selectedFileIds.size < files.length;
    const hasFailedFiles = files.some(f => f.sync_status === 'failed' || f.sync_status === 'error');
    
    return { allSelected, someSelected, hasFailedFiles };
  }, [files, selectedFileIds]);

  const theme = getThemeClasses();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingIndicator type="spinner" size="medium" />
      </div>
    );
  }

  return (
    <ErrorBoundary 
      fallbackMessage="Unable to load file list. Please refresh the page."
      context={{ component: 'FileListModule', context }}
    >
      <div className="space-y-4">
        {/* Operation Status Display */}
        {operationStatus.size > 0 && (
          <div className="space-y-2">
            {Array.from(operationStatus.entries()).map(([key, status]) => (
              <OperationStatus
                key={key}
                operation={status.message}
                status={status.status}
                onRetry={status.onRetry}
              />
            ))}
          </div>
        )}

        {/* Bulk Operations Header */}
        {allowBulkOperations && allowSelection && files.length > 0 && (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <div className="flex items-center gap-4">
              {allowSelection && (
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={input => {
                      if (input) input.indeterminate = someSelected;
                    }}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded dark:border-gray-600"
                    aria-label={`Select all ${files.length} files`}
                  />
                  <span className={`ml-2 text-sm ${theme.text.secondary}`}>
                    Select all ({files.length})
                  </span>
                </label>
              )}
              
              {selectedFileIds.size > 0 && (
                <span className={`text-sm ${theme.text.secondary}`}>
                  {selectedFileIds.size} selected
                </span>
              )}
            </div>
            
            {selectedFileIds.size > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleBulkOperation('delete')}
                  disabled={bulkOperationInProgress}
                  className={getButtonClasses('danger', 'small', bulkOperationInProgress)}
                >
                  Delete Selected
                </button>
                
                {hasFailedFiles && (
                  <button
                    onClick={() => handleBulkOperation('retry-sync')}
                    disabled={bulkOperationInProgress}
                    className={getButtonClasses('secondary', 'small', bulkOperationInProgress)}
                  >
                    Retry Sync
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Files list */}
        {files.length === 0 ? (
          <div className="text-center py-12">
            <div className={theme.text.muted}>
              {emptyMessage || 'No files found'}
            </div>
          </div>
        ) : (
          <div className={getCardClasses()}>
            <ul className={`divide-y ${theme.border.primary}`}>
              {files.map((file) => {
                const fileIcon = getFileTypeIcon(file);
                const isTextFile = file.type === 'text' || file.mime_type === 'text/plain';
                
                return (
                  <li key={file.id} className="px-4 sm:px-6 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex items-start flex-1 min-w-0">
                        {allowSelection && (
                          <input
                            type="checkbox"
                            checked={selectedFileIds.has(file.id)}
                            onChange={(e) => handleFileSelection(file.id, e.target.checked)}
                            className="mr-3 mt-1 h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded flex-shrink-0 dark:border-gray-600"
                          />
                        )}
                        
                        {/* File type icon */}
                        <div className={`mr-3 mt-0.5 p-2 rounded-lg ${fileIcon.bgColor} flex-shrink-0`}>
                          <div className={fileIcon.color}>
                            {fileIcon.icon}
                          </div>
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium truncate ${theme.text.primary}`}>
                                {file.name}
                              </p>
                              <div className={`mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm ${theme.text.secondary}`}>
                                <span className="flex-shrink-0 capitalize">{file.type}</span>
                                {file.size && (
                                  <>
                                    <span className="hidden sm:inline">•</span>
                                    <span className="flex-shrink-0">{file.formattedSize || formatFileSize(file.size)}</span>
                                  </>
                                )}
                                <span className="hidden sm:inline">•</span>
                                <span className="flex-shrink-0">{new Date(file.created_at).toLocaleDateString()}</span>
                              </div>
                              
                              {/* Parrot usage information */}
                              {showUsage && (
                                <div className="mt-2">
                                  {(file.parrotUsage && file.parrotUsage.count > 0) ? (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300">
                                      <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                      </svg>
                                      <span className="truncate">
                                        Used by {file.parrotUsage.count} parrot{file.parrotUsage.count !== 1 ? 's' : ''}: {file.parrotUsage.parrotNames.join(', ')}
                                      </span>
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-400">
                                      <svg className="w-3 h-3 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                      </svg>
                                      <span className="truncate">
                                        Assigned To: Not Assigned
                                      </span>
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            <div className="flex items-center space-x-2 flex-shrink-0">
                              {/* Edit button for text files */}
                              {allowEdit && isTextFile && onFileEdit && (
                                <button
                                  onClick={() => handleEditFile(file)}
                                  className={`text-sm px-2 py-1 rounded transition-colors ${theme.text.link} hover:bg-teal-50 dark:hover:bg-teal-900/20`}
                                >
                                  Edit
                                </button>
                              )}
                              
                              {/* Delete button */}
                              {onFileDelete && (
                                <button
                                  onClick={() => showDeleteConfirmation(file)}
                                  disabled={deletingFiles.has(file.id)}
                                  className={`text-sm px-2 py-1 rounded transition-colors ${
                                    deletingFiles.has(file.id) 
                                      ? `${theme.text.muted} cursor-not-allowed` 
                                      : `${theme.text.error} hover:bg-red-50 dark:hover:bg-red-900/20`
                                  }`}
                                >
                                  {deletingFiles.has(file.id) ? (
                                    <div className="flex items-center">
                                      <LoadingIndicator type="spinner" size="small" color="secondary" />
                                      <span className="ml-2">Deleting...</span>
                                    </div>
                                  ) : (
                                    'Delete'
                                  )}
                                </button>
                              )}
                            </div>
                          </div>
                          
                          {/* Enhanced sync status */}
                          {showSyncStatus && (
                            <div className="mt-3">
                              <EnhancedKnowledgeBaseSyncStatus
                                file={file}
                                onRetry={handleSyncRetry}
                                compact={true}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <FileDeleteConfirmationDialog
          isOpen={confirmDialog.isOpen}
          file={confirmDialog.file}
          parrotUsage={confirmDialog.parrotUsage}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
          loading={confirmDialog.file ? deletingFiles.has(confirmDialog.file.id) : false}
        />
      </div>
    </ErrorBoundary>
  );
};

export default FileListModule;